import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface ModelFormData {
  brandId: string;
  code: string;
  name: string;
  slug: string;
  description?: string;
  image?: string;
  isActive: boolean;
  sortOrder: number;
}

interface TradingModel {
  id: string;
  brandId: string;
  code: string;
  name: string;
  slug: string;
  description?: string | null;
  image?: string | null;
  isActive: boolean;
  sortOrder: number;
}

interface ModelModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  model?: TradingModel;
  defaultBrandId?: string;
}

export function ModelModal({ open, onOpenChange, model, defaultBrandId }: ModelModalProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const isEditing = !!model;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ModelFormData>({
    defaultValues: {
      brandId: defaultBrandId || '',
      code: '',
      name: '',
      slug: '',
      description: '',
      image: '',
      isActive: true,
      sortOrder: 0,
    },
  });

  const utils = trpc.useUtils();

  // Get brands for dropdown
  const { data: brandsData } = trpc.tradingBrand.list.useQuery({});
  const brands = brandsData?.brands ?? [];

  const watchBrandId = watch('brandId');

  // Get next available code for new models
  const { data: nextCodeData } = trpc.tradingModel.getNextCode.useQuery(
    watchBrandId,
    { enabled: open && !isEditing && !!watchBrandId }
  );

  const createMutation = trpc.tradingModel.create.useMutation({
    onSuccess: () => {
      utils.tradingModel.list.invalidate();
      utils.tradingBrand.list.invalidate();
      showSuccessToast(t('common.created'));
      onOpenChange(false);
      reset();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.tradingModel.update.useMutation({
    onSuccess: () => {
      utils.tradingModel.list.invalidate();
      showSuccessToast(t('common.updated'));
      onOpenChange(false);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  useEffect(() => {
    if (open) {
      if (model) {
        reset({
          brandId: model.brandId,
          code: model.code,
          name: model.name,
          slug: model.slug,
          description: model.description || '',
          image: model.image || '',
          isActive: model.isActive,
          sortOrder: model.sortOrder,
        });
      } else {
        reset({
          brandId: defaultBrandId || '',
          code: nextCodeData?.code || '',
          name: '',
          slug: '',
          description: '',
          image: '',
          isActive: true,
          sortOrder: nextCodeData?.sortOrder || 0,
        });
      }
    }
  }, [open, model, nextCodeData, defaultBrandId, reset]);

  // Update code when brand changes
  useEffect(() => {
    if (!isEditing && nextCodeData) {
      setValue('code', nextCodeData.code);
      setValue('sortOrder', nextCodeData.sortOrder);
    }
  }, [nextCodeData, isEditing, setValue]);

  // Auto-generate slug from name
  const watchName = watch('name');
  useEffect(() => {
    if (!isEditing && watchName) {
      const slug = watchName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setValue('slug', slug);
    }
  }, [watchName, isEditing, setValue]);

  const onSubmit = (data: ModelFormData) => {
    const payload = {
      ...data,
      description: data.description || undefined,
      image: data.image || undefined,
    };

    if (isEditing && model) {
      updateMutation.mutate({
        id: model.id,
        data: payload,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? t('models.editModel') : t('models.addModel')}
          </DialogTitle>
          <DialogDescription>
            {isEditing ? t('models.editModel') : t('models.subtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="brandId">{t('models.brand')}</Label>
            <select
              id="brandId"
              {...register('brandId', { required: t('common.required') })}
              disabled={isEditing}
              className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">{t('models.selectBrand')}</option>
              {brands?.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.code} - {brand.name}
                </option>
              ))}
            </select>
            {errors.brandId && (
              <p className="text-sm text-red-500">{errors.brandId.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="code">{t('models.code')}</Label>
              <Input
                id="code"
                {...register('code', { required: t('common.required') })}
                placeholder="01"
                maxLength={2}
                disabled={isEditing}
              />
              {errors.code && (
                <p className="text-sm text-red-500">{errors.code.message}</p>
              )}
              <p className="text-xs text-muted-foreground">{t('models.codeHelp')}</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="sortOrder">{t('models.sortOrder')}</Label>
              <Input
                id="sortOrder"
                type="number"
                {...register('sortOrder', { valueAsNumber: true })}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">{t('models.name')}</Label>
            <Input
              id="name"
              {...register('name', { required: t('common.required') })}
              placeholder="Model Name"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="slug">{t('models.slug')}</Label>
            <Input
              id="slug"
              {...register('slug', { required: t('common.required') })}
              placeholder="model-name"
            />
            {errors.slug && (
              <p className="text-sm text-red-500">{errors.slug.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">{t('models.description')}</Label>
            <Textarea
              id="description"
              {...register('description')}
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="image">{t('models.image')}</Label>
            <Input
              id="image"
              {...register('image')}
              placeholder="https://example.com/image.png"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              {...register('isActive')}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="isActive">{t('models.active')}</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting || !watchBrandId}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isEditing ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
